import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CornerUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SignoffHint } from "@/pages/inbox/InboxActions";

// `forward(messageId, { to, comment, move_to_done })` → { moved_to_done }
export function ForwardDialog({ email, forward, onClose, onForwarded, showSignoff = false, domain }) {
  const [to, setTo] = useState("");
  const [comment, setComment] = useState("");
  const [alsoDone, setAlsoDone] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const recipients = to.split(/[,;\s]+/).map(s => s.trim()).filter(s => s.includes("@"));
    if (!recipients.length) { toast.error("Enter at least one email address"); return; }
    setSending(true);
    try {
      const r = await forward(email.id, { to: recipients, comment, move_to_done: alsoDone });
      toast.success(`Forwarded to ${recipients.join(", ")}${r.moved_to_done ? " · moved to Done" : ""}`);
      onForwarded(r.moved_to_done);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg overflow-hidden" aria-describedby={undefined} data-testid="forward-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <CornerUpRight className="w-4 h-4 text-blue-600" />Forward email
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 min-w-0">
          <div className="text-xs text-muted-foreground border rounded px-2 py-1.5 bg-slate-50 min-w-0" title={email.subject || ""}>
            <div className="truncate font-medium text-slate-700">{email.subject || "(no subject)"}</div>
            <div className="text-[11px]">Attachments are forwarded automatically</div>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">To (comma-separated)</label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder={`name@${domain || "simplyfunded.co.uk"}`}
              className="h-8 text-xs" data-testid="forward-to-input" autoFocus />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">Note (optional, goes above the forwarded email)</label>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="text-xs" data-testid="forward-comment" />
            {showSignoff && <div className="mt-1"><SignoffHint /></div>}
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={alsoDone} onChange={(e) => setAlsoDone(e.target.checked)} data-testid="forward-move-done" />
            Move the email to Done after forwarding
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={sending || !to.trim()} data-testid="forward-submit">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CornerUpRight className="w-4 h-4 mr-1" />}
            {sending ? "Forwarding…" : "Forward"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
